"use client";

import type React from "react";
import { ArrowUpRight } from "lucide-react";
import { resolveProfileLink, sortProfileLinks } from "@/lib/link-types";
import {
  analyzeThemeSection,
  getInlineText,
  hashThemeText,
  type ThemeEntry,
  type ThemeSectionLayout
} from "@/lib/theme-structure";
import type { MarkdownSection, ProfileLink, ProfileRenderModel } from "@/lib/types";
import { useI18n } from "../I18nProvider";
import { MarkdownBody, MarkdownInline, MarkdownInlineText } from "../MarkdownBody";
import {
  LinkActionIcon,
  ProfileLinkItem,
  ThemeFooter,
  type ThemeRendererProps
} from "./theme-primitives";

/* --- Shared structure ---------------------------------------------------- */

type EntryArt = (entry: ThemeEntry, index: number) => React.ReactNode;

function sparseClass(model: ProfileRenderModel, links: ProfileLink[]): string {
  return model.sections.length === 0 && links.length === 0 ? " theme-v2-sparse" : "";
}

function handleLabel(model: ProfileRenderModel): string {
  return `30s.to/@${model.profile.handle || "handle"}`;
}

function SectionTitle({ section, className }: { section: MarkdownSection; className?: string }) {
  if (section.kind === "default") return null;
  return <h2 className={className}>{section.title}</h2>;
}

function StructuredBody({
  layout,
  prefix,
  entryArt,
  numbered = false
}: {
  layout: ThemeSectionLayout;
  prefix: string;
  entryArt?: EntryArt;
  numbered?: boolean;
}) {
  if (layout.type === "prose") {
    return (
      <div className={`${prefix}-section-body`} data-layout="prose">
        <MarkdownBody nodes={layout.nodes} className={`${prefix}-prose`} />
      </div>
    );
  }

  if (layout.type === "timeline") {
    return (
      <div className={`${prefix}-section-body`} data-layout="timeline">
        {layout.lead.length > 0 && <MarkdownBody nodes={layout.lead} className={`${prefix}-prose`} />}
        <ol className={`${prefix}-timeline`}>
          {layout.items.map((item, index) => (
            <li className={`${prefix}-timeline-item`} key={index}>
              <span className={`${prefix}-period`} aria-hidden={item.period ? undefined : true}>{item.period}</span>
              <div className={`${prefix}-timeline-body`}>
                <p><MarkdownInline nodes={item.body} keyPrefix={`t-${index}`} /></p>
                {item.extra.length > 0 && <MarkdownBody nodes={item.extra} className={`${prefix}-prose`} />}
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  return (
    <div className={`${prefix}-section-body`} data-layout="entries">
      {layout.lead.length > 0 && <MarkdownBody nodes={layout.lead} className={`${prefix}-prose`} />}
      <ul className={`${prefix}-entries`} data-count={layout.entries.length}>
        {layout.entries.map((entry, index) => (
          <li className={`${prefix}-entry`} key={index}>
            {entryArt?.(entry, index)}
            <div className={`${prefix}-entry-body`}>
              {numbered && <span className={`${prefix}-entry-index`} aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>}
              <h3 className={`${prefix}-entry-title`}>
                {entry.href ? (
                  <a href={entry.href} rel="noreferrer">
                    <span><MarkdownInlineText nodes={entry.title} keyPrefix={`e-${index}`} /></span>
                    <ArrowUpRight size={15} aria-hidden="true" />
                  </a>
                ) : (
                  <MarkdownInline nodes={entry.title} keyPrefix={`e-${index}`} />
                )}
              </h3>
              {entry.description.length > 0 && (
                <p className={`${prefix}-entry-desc`}><MarkdownInline nodes={entry.description} keyPrefix={`d-${index}`} /></p>
              )}
              {entry.extra.length > 0 && <MarkdownBody nodes={entry.extra} className={`${prefix}-prose`} />}
            </div>
          </li>
        ))}
      </ul>
      {layout.tail.length > 0 && <MarkdownBody nodes={layout.tail} className={`${prefix}-prose`} />}
    </div>
  );
}

function ThemeLinks({
  prefix,
  links,
  heading,
  interactiveLinks,
  onLinkClick,
  t,
  numbered = false
}: {
  prefix: string;
  links: ProfileLink[];
  heading: React.ReactNode;
  interactiveLinks: boolean;
  onLinkClick?: (link: ProfileLink) => void;
  t: ThemeRendererProps["t"];
  numbered?: boolean;
}) {
  if (links.length === 0) return null;
  return (
    <nav className={`${prefix}-links`} aria-label={t("theme.connect")}>
      {heading}
      <ul className={`${prefix}-link-list`}>
        {links.map((link, index) => (
          <li key={link.id}>
            <ProfileLinkItem link={link} interactiveLinks={interactiveLinks} onLinkClick={onLinkClick} className={`${prefix}-link`}>
              {numbered && <small aria-hidden="true">{String(index + 1).padStart(2, "0")}</small>}
              <span className={`${prefix}-link-label`}>{link.label}</span>
              <LinkActionIcon action={resolveProfileLink(link).action} link={link} />
            </ProfileLinkItem>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Two-column themes give structured sections the full row. Consecutive prose sections pair up;
 * an unpaired last prose section also takes the full row so the grid never leaves a hole.
 */
function computeWideSections(layouts: ThemeSectionLayout[]): boolean[] {
  const wide = layouts.map((layout) => layout.type !== "prose");
  let runStart = -1;
  for (let index = 0; index <= layouts.length; index += 1) {
    const isProse = index < layouts.length && !wide[index];
    if (isProse && runStart < 0) runStart = index;
    if (!isProse && runStart >= 0) {
      if ((index - runStart) % 2 === 1) wide[index - 1] = true;
      runStart = -1;
    }
  }
  return wide;
}

function readingLabel(model: ProfileRenderModel, t: ThemeRendererProps["t"]): string {
  return t("theme.readingTime", { seconds: Math.max(1, Math.round(model.reading.seconds)) });
}

/* --- Folio: quiet modern portfolio ---------------------------------------- */

export function FolioTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const seconds = Math.max(1, Math.round(model.reading.seconds));
  const progress = Math.min(100, Math.round((seconds / 60) * 100));

  return (
    <article className={`theme-v2-wrapper theme-v2-folio${sparseClass(model, links)}`} data-theme="folio">
      <div className="folio-sheet">
        <header className="folio-header">
          <div className="folio-meta">
            <strong>{handleLabel(model)}</strong>
            <span className="folio-reading">
              <i className="folio-mark" aria-hidden="true" style={{ "--folio-progress": `${progress}%` } as React.CSSProperties} />
              {readingLabel(model, t)}
            </span>
          </div>
          <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
          <p className="folio-identity">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          {model.profile.tags.length > 0 && (
            <ul className="folio-tags" aria-label={t("theme.tags")}>
              {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
            </ul>
          )}
        </header>
        {model.sections.length > 0 && (
          <div className="folio-sections">
            {model.sections.map((section) => (
              <section className="folio-section" data-kind={section.kind} key={section.id}>
                <SectionTitle section={section} className="folio-section-title" />
                <StructuredBody layout={analyzeThemeSection(section)} prefix="folio" />
              </section>
            ))}
          </div>
        )}
        <ThemeLinks
          prefix="folio"
          links={links}
          heading={<h2 className="folio-section-title">{t("theme.connect")}</h2>}
          interactiveLinks={interactiveLinks}
          onLinkClick={onLinkClick}
          t={t}
        />
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

/* --- Ink: CJK-first literati scroll ----------------------------------------- */

const INK_NUMERALS = ["壹", "贰", "叁", "肆", "伍", "陆", "柒", "捌", "玖", "拾"];

export function InkTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const name = model.profile.displayName || t("theme.fallback.name");
  const sealGlyph = Array.from(model.profile.displayName.trim())[0]?.toLocaleUpperCase() || "印";

  return (
    <article className={`theme-v2-wrapper theme-v2-ink${sparseClass(model, links)}`} data-theme="ink">
      <div className="ink-paper" aria-hidden="true" />
      <div className="ink-scroll">
        <header className="ink-header">
          <div className="ink-meta">
            <span className="ink-handle">{handleLabel(model)}</span>
            <span className="ink-seal" aria-hidden="true"><b>{sealGlyph}</b></span>
          </div>
          <h1>{name}</h1>
          <p className="ink-identity">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          {model.profile.tags.length > 0 && (
            <ul className="ink-tags" aria-label={t("theme.tags")}>
              {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
            </ul>
          )}
        </header>
        <div className="ink-flow">
          {model.sections.length > 0 && (
            <div className="ink-sections">
              {model.sections.map((section, index) => (
                <section className="ink-section" data-kind={section.kind} key={section.id}>
                  {section.kind !== "default" && (
                    <div className="ink-section-heading">
                      <span aria-hidden="true">{INK_NUMERALS[index] ?? String(index + 1)}</span>
                      <h2>{section.title}</h2>
                    </div>
                  )}
                  <StructuredBody layout={analyzeThemeSection(section)} prefix="ink" />
                </section>
              ))}
            </div>
          )}
          <ThemeLinks
            prefix="ink"
            links={links}
            heading={<h2 className="ink-links-title">{t("theme.connect")}</h2>}
            interactiveLinks={interactiveLinks}
            onLinkClick={onLinkClick}
            t={t}
          />
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

/* --- Bento: structure as layout --------------------------------------------- */

export function BentoTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const seconds = Math.max(1, Math.round(model.reading.seconds));
  const layouts = model.sections.map(analyzeThemeSection);
  const wide = computeWideSections(layouts);

  return (
    <article className={`theme-v2-wrapper theme-v2-bento${sparseClass(model, links)}`} data-theme="bento">
      <div className="bento-shell">
        <div className="bento-grid">
          <header className="bento-tile bento-hero">
            <div className="bento-handle">
              <i className="bento-pulse" aria-hidden="true" />
              <span>{handleLabel(model)}</span>
            </div>
            <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
            <p>{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          </header>
          <div className={model.profile.tags.length > 0 ? "bento-tile bento-stat" : "bento-tile bento-stat bento-stat-wide"}>
            <strong>{seconds}</strong>
            <span>{t("theme.readingUnit")}</span>
          </div>
          {model.profile.tags.length > 0 && (
            <ul className="bento-tile bento-tags" aria-label={t("theme.tags")}>
              {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
            </ul>
          )}
          {model.sections.map((section, index) => {
            const layout = layouts[index];
            return (
              <section
                className={`bento-tile bento-section${wide[index] ? " bento-section-wide" : ""}`}
                data-kind={section.kind}
                data-layout={layout.type}
                key={section.id}
              >
                <SectionTitle section={section} className="bento-section-title" />
                <StructuredBody layout={layout} prefix="bento" />
              </section>
            );
          })}
          <ThemeLinks
            prefix="bento"
            links={links}
            heading={<h2 className="bento-links-title">{t("theme.connect")}</h2>}
            interactiveLinks={interactiveLinks}
            onLinkClick={onLinkClick}
            t={t}
          />
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

/* --- Letter: a first-person introduction letter --------------------------- */

export function LetterTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const { locale } = useI18n();
  const links = sortProfileLinks(model.profile.links);
  const name = model.profile.displayName || t("theme.fallback.name");
  const writtenAt = formatLetterDate(model.profile.publishedAt ?? model.profile.updatedAt, locale);

  return (
    <article className={`theme-v2-wrapper theme-v2-letter${sparseClass(model, links)}`} data-theme="letter">
      <div className="letter-desk">
        <div className="letter-sheet">
          <header className="letter-header">
            <div className="letter-meta">
              <div className="letter-address">
                <strong>{handleLabel(model)}</strong>
                {writtenAt && <span>{t("theme.letter.written", { date: writtenAt })}</span>}
              </div>
              <div className="letter-stamp" aria-hidden="true">
                <span>30s</span>
                <small>{model.profile.handle || "to"}</small>
              </div>
            </div>
            <p className="letter-greeting">{t("theme.letter.greeting")}</p>
            <h1>{name}</h1>
            <p className="letter-identity">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
            {model.profile.tags.length > 0 && (
              <ul className="letter-tags" aria-label={t("theme.tags")}>
                {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
              </ul>
            )}
          </header>
          {model.sections.length > 0 && (
            <div className="letter-sections">
              {model.sections.map((section) => (
                <section className="letter-section" data-kind={section.kind} key={section.id}>
                  <SectionTitle section={section} className="letter-section-title" />
                  <StructuredBody layout={analyzeThemeSection(section)} prefix="letter" />
                </section>
              ))}
            </div>
          )}
          <div className="letter-closing">
            <span>{t("theme.letter.signoff")}</span>
            <strong className="letter-signature" aria-hidden="true">{name}</strong>
          </div>
          <ThemeLinks
            prefix="letter"
            links={links}
            heading={(
              <div className="letter-ps">
                <b aria-hidden="true">P.S.</b>
                <h2>{t("theme.letter.postscript")}</h2>
              </div>
            )}
            interactiveLinks={interactiveLinks}
            onLinkClick={onLinkClick}
            t={t}
          />
        </div>
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

function formatLetterDate(value: string | undefined, locale: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  try {
    return new Intl.DateTimeFormat(locale, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

/* --- Exhibit: gallery wall labels -------------------------------------------- */

const EXHIBIT_COLORS = ["#d9482b", "#1f4e8c", "#e3b23c", "#2f6b4f", "#c9a7d8", "#141414", "#e8ddc9", "#8a3b2e", "#9fc2c7"];
const EXHIBIT_SHAPES = ["orb", "arch", "bands", "split"] as const;

function ExhibitArt({ seed, className }: { seed: string; className: string }) {
  const hash = hashThemeText(seed || "30s.to");
  const ground = EXHIBIT_COLORS[hash % EXHIBIT_COLORS.length];
  let figure = EXHIBIT_COLORS[(hash >>> 5) % EXHIBIT_COLORS.length];
  if (figure === ground) figure = EXHIBIT_COLORS[(hash + 3) % EXHIBIT_COLORS.length];
  const accent = EXHIBIT_COLORS[(hash >>> 11) % EXHIBIT_COLORS.length];
  const shape = EXHIBIT_SHAPES[(hash >>> 17) % EXHIBIT_SHAPES.length];
  const style = {
    "--exhibit-ground": ground,
    "--exhibit-figure": figure,
    "--exhibit-accent": accent,
    "--exhibit-shift": `${18 + ((hash >>> 21) % 46)}%`
  } as React.CSSProperties;

  return (
    <div className={className} data-shape={shape} style={style} aria-hidden="true">
      <i />
      <b />
    </div>
  );
}

export function ExhibitTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const name = model.profile.displayName || t("theme.fallback.name");
  const entryArt: EntryArt = (entry) => (
    <ExhibitArt seed={getInlineText(entry.title)} className="exhibit-work-art" />
  );

  return (
    <article className={`theme-v2-wrapper theme-v2-exhibit${sparseClass(model, links)}`} data-theme="exhibit">
      <div className="exhibit-wall">
        <header className="exhibit-header">
          <div className="exhibit-meta">
            <strong>{handleLabel(model)}</strong>
          </div>
          <div className="exhibit-hero">
            <ExhibitArt seed={`${model.profile.handle}:${name}`} className="exhibit-artwork" />
            <div className="exhibit-label">
              <h1>{name}</h1>
              <p className="exhibit-identity">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
              {model.profile.tags.length > 0 && (
                <ul className="exhibit-tags" aria-label={t("theme.tags")}>
                  {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
                </ul>
              )}
            </div>
          </div>
        </header>
        {model.sections.length > 0 && (
          <div className="exhibit-sections">
            {model.sections.map((section) => {
              const layout = analyzeThemeSection(section);
              return (
                <section className="exhibit-section" data-kind={section.kind} data-layout={layout.type} key={section.id}>
                  <SectionTitle section={section} className="exhibit-section-title" />
                  <StructuredBody layout={layout} prefix="exhibit" entryArt={entryArt} />
                </section>
              );
            })}
          </div>
        )}
        <ThemeLinks
          prefix="exhibit"
          links={links}
          heading={<h2 className="exhibit-section-title">{t("theme.connect")}</h2>}
          interactiveLinks={interactiveLinks}
          onLinkClick={onLinkClick}
          t={t}
        />
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}

/* --- Blueprint: engineering drawing -------------------------------------------- */

export function BlueprintTheme({ model, interactiveLinks, onLinkClick, showAttribution, t }: ThemeRendererProps) {
  const links = sortProfileLinks(model.profile.links);
  const layouts = model.sections.map(analyzeThemeSection);
  const wide = computeWideSections(layouts);

  return (
    <article className={`theme-v2-wrapper theme-v2-blueprint${sparseClass(model, links)}`} data-theme="blueprint">
      <div className="blueprint-grid" aria-hidden="true" />
      <div className="blueprint-sheet">
        <header className="blueprint-header">
          <div className="blueprint-meta">
            <strong>{handleLabel(model)}</strong>
            {model.profile.tags.length > 0 && <span aria-hidden="true">{model.profile.tags.length} SPEC</span>}
          </div>
          <div className="blueprint-part">
            <i className="blueprint-corner blueprint-corner-a" aria-hidden="true" />
            <i className="blueprint-corner blueprint-corner-b" aria-hidden="true" />
            <h1>{model.profile.displayName || t("theme.fallback.name")}</h1>
            <p className="blueprint-identity">{model.profile.oneLineIdentity || t("theme.fallback.identity")}</p>
          </div>
          <div className="blueprint-dimension" aria-hidden="true">
            <i />
            <span>{readingLabel(model, t)}</span>
            <i />
          </div>
          {model.profile.tags.length > 0 && (
            <ul className="blueprint-tags" aria-label={t("theme.tags")}>
              {model.profile.tags.map((tag) => <li key={tag}>{tag}</li>)}
            </ul>
          )}
        </header>
        {model.sections.length > 0 && (
          <div className="blueprint-sections">
            {model.sections.map((section, index) => (
              <section
                className={`blueprint-section${wide[index] ? " blueprint-section-wide" : ""}`}
                data-kind={section.kind}
                data-layout={layouts[index].type}
                key={section.id}
              >
                <div className="blueprint-section-heading">
                  <span aria-hidden="true">{String.fromCharCode(65 + (index % 26))}</span>
                  {section.kind !== "default" && <h2>{section.title}</h2>}
                </div>
                <StructuredBody layout={layouts[index]} prefix="blueprint" numbered />
              </section>
            ))}
          </div>
        )}
        <ThemeLinks
          prefix="blueprint"
          links={links}
          heading={(
            <div className="blueprint-section-heading">
              <span aria-hidden="true">↔</span>
              <h2>{t("theme.connect")}</h2>
            </div>
          )}
          interactiveLinks={interactiveLinks}
          onLinkClick={onLinkClick}
          t={t}
          numbered
        />
        <ThemeFooter showAttribution={showAttribution} t={t} />
      </div>
    </article>
  );
}
